import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { CalendarClock, Video, CheckCircle2, Building2, Briefcase, Clock, ExternalLink, AlertTriangle } from 'lucide-react';
import { interviewsAPI } from '@/utils/api';
import { prospectDate, localConversion } from '@/utils/prospectTime';

const locale = (lang) => (lang === 'en' ? 'en-GB' : lang === 'fr' ? 'fr-FR' : 'pt-PT');
const CV = 'Atlantic/Cape_Verde';
const fmtDay = (iso, lang) => prospectDate(iso).toLocaleDateString(locale(lang), { weekday: 'long', day: 'numeric', month: 'long', timeZone: CV });
const fmtTime = (iso, lang) => `${prospectDate(iso).toLocaleTimeString(locale(lang), { hour: '2-digit', minute: '2-digit', timeZone: CV })} (hora de Cabo Verde)`;
const LocalHint = ({ iso, testid }) => { const c = localConversion(iso); return c ? <span className="block text-[11px] text-amber-700" data-testid={testid}>{c}</span> : null; };

export default function InterviewSchedule() {
  const { token } = useParams();
  const { t, i18n } = useTranslation();
  const [iv, setIv] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState('');

  useEffect(() => {
    interviewsAPI.publicGet(token).then(({ data }) => setIv(data)).catch((e) => setError(e?.response?.data?.detail || t('interviews.public.invalid'))).finally(() => setLoading(false));
  }, [token, t]);

  const choose = async () => {
    if (selected === null) return;
    setBusy(true); setError('');
    try { const { data } = await interviewsAPI.publicChoose(token, selected); setIv(data); }
    catch (e) { setError(e?.response?.data?.detail || t('interviews.public.errChoose')); }
    finally { setBusy(false); }
  };
  const decline = async () => {
    setBusy(true); setError('');
    try { await interviewsAPI.publicDecline(token, reason || null); setIv({ ...iv, status: 'needs_reschedule' }); }
    catch (e) { setError(e?.response?.data?.detail || t('interviews.public.errChoose')); }
    finally { setBusy(false); }
  };

  return (
    <main className="min-h-screen bg-[#0A2342] py-10 px-4" data-testid="interview-schedule-page">
      <Helmet><title>{`${t('interviews.public.title')} · Trabadju`}</title><meta name="robots" content="noindex,nofollow" /></Helmet>
      <div className="max-w-xl mx-auto">
        <div className="text-center mb-6">
          <Link to="/" className="text-3xl font-extrabold tracking-tight text-white">Trab<span className="text-amber-400">adju</span><span className="text-sm text-blue-200 font-semibold">.cv</span></Link>
        </div>
        <Card className="p-7 sm:p-9 rounded-2xl shadow-2xl border-0">
          {loading ? <p className="text-center text-gray-500 py-10">{t('interviews.loading')}</p>
            : !iv ? (
              <div className="text-center py-6" data-testid="interview-invalid">
                <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto mb-3" />
                <h1 className="text-xl font-bold mb-2">{t('interviews.public.invalidTitle')}</h1>
                <p className="text-gray-600">{error}</p>
              </div>
            ) : (
              <>
                <p className="text-xs font-semibold tracking-widest text-emerald-700 uppercase mb-1">{t('interviews.public.eyebrow')}</p>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 leading-tight mb-3" data-testid="interview-heading">
                  {iv.status === 'scheduled' || iv.status === 'completed' ? t('interviews.public.confirmedTitle') : iv.status === 'needs_reschedule' ? t('interviews.public.declinedTitle') : t('interviews.public.title')}
                </h1>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-700 mb-5">
                  <span className="inline-flex items-center gap-1.5"><Building2 className="h-4 w-4 text-gray-400" />{iv.company_name}</span>
                  <span className="inline-flex items-center gap-1.5"><Briefcase className="h-4 w-4 text-gray-400" />{iv.job_title}</span>
                  <span className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4 text-gray-400" />{iv.duration_min} min</span>
                </div>
                {iv.note && <p className="text-sm text-gray-700 bg-slate-50 border-l-4 border-slate-300 px-3 py-2 rounded-r-md mb-5">{iv.note}</p>}

                {iv.status === 'proposed' && !declining && (
                  <>
                    <p className="text-sm font-semibold text-gray-800 mb-2">{t('interviews.public.pick', { name: iv.candidate_name })}</p>
                    <div className="space-y-2 mb-5" data-testid="slot-options">
                      {iv.slots.map((s, i) => (
                        <button key={i} type="button" onClick={() => setSelected(i)} data-testid={`slot-option-${i}`}
                          className={`w-full text-left flex items-center gap-3 border-2 rounded-xl px-4 py-3 transition-colors ${selected === i ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:border-blue-300'}`}>
                          <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selected === i ? 'border-blue-600 bg-blue-600' : 'border-gray-300'}`}>{selected === i && <span className="w-2 h-2 rounded-full bg-white" />}</span>
                          <span><span className="block font-semibold text-gray-900 capitalize">{fmtDay(s.start_at, i18n.language)}</span><span className="text-sm text-gray-600">{fmtTime(s.start_at, i18n.language)} · {s.duration_min} min</span><LocalHint iso={s.start_at} testid={`slot-local-${i}`} /></span>
                        </button>
                      ))}
                    </div>
                    {error && <p className="text-sm text-red-600 mb-3" data-testid="choose-error">{error}</p>}
                    <Button onClick={choose} disabled={selected === null || busy} className="w-full h-12 text-base bg-amber-400 hover:bg-amber-500 text-[#0A2342] font-bold" data-testid="confirm-slot-btn">
                      <Video className="h-5 w-5 mr-2" />{busy ? t('interviews.public.confirming') : t('interviews.public.confirm')}
                    </Button>
                    <button type="button" onClick={() => setDeclining(true)} className="mt-3 w-full text-sm text-gray-500 hover:text-gray-800 underline" data-testid="decline-link">{t('interviews.public.noneWork')}</button>
                  </>
                )}

                {iv.status === 'proposed' && declining && (
                  <div data-testid="decline-form">
                    <p className="text-sm font-semibold text-gray-800 mb-2">{t('interviews.public.declineTitle')}</p>
                    <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t('interviews.public.declinePlaceholder')} data-testid="decline-reason" className="mb-3" />
                    {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={() => setDeclining(false)} className="flex-1" data-testid="decline-back">{t('interviews.public.back')}</Button>
                      <Button onClick={decline} disabled={busy} className="flex-1 bg-[#0A2342] hover:bg-[#123566]" data-testid="decline-submit">{t('interviews.public.declineSubmit')}</Button>
                    </div>
                  </div>
                )}

                {(iv.status === 'scheduled' || iv.status === 'completed') && (
                  <div data-testid="interview-confirmed">
                    <div className="flex items-start gap-3 bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-4">
                      <CheckCircle2 className="h-6 w-6 text-emerald-600 flex-shrink-0" />
                      <div>
                        <p className="font-bold text-emerald-900 capitalize" data-testid="confirmed-when">{fmtDay(iv.scheduled_start, i18n.language)} · {fmtTime(iv.scheduled_start, i18n.language)}</p>
                        <LocalHint iso={iv.scheduled_start} testid="confirmed-local" />
                        <p className="text-sm text-emerald-800">{t('interviews.public.emailSent')}</p>
                      </div>
                    </div>
                    {iv.video?.join_url && (
                      <a href={iv.video.join_url} target="_blank" rel="noopener noreferrer" data-testid="join-link"
                        className="flex items-center justify-center gap-2 w-full h-12 rounded-md bg-blue-600 hover:bg-blue-700 text-white font-semibold">
                        <Video className="h-5 w-5" />{t('interviews.join')} ({iv.video.provider === 'zoom' ? 'Zoom' : 'Jitsi Meet'})<ExternalLink className="h-4 w-4" />
                      </a>
                    )}
                    {iv.video?.password && <p className="text-center text-sm text-gray-600 mt-2">{t('interviews.password')}: <strong>{iv.video.password}</strong></p>}
                    <p className="text-xs text-gray-500 text-center mt-4 break-all">{iv.video?.join_url}</p>
                  </div>
                )}

                {iv.status === 'needs_reschedule' && (
                  <div className="text-center py-4" data-testid="interview-declined">
                    <CalendarClock className="h-10 w-10 text-orange-500 mx-auto mb-3" />
                    <p className="font-semibold text-gray-900">{t('interviews.public.declinedTitle')}</p>
                    <p className="text-sm text-gray-600">{t('interviews.public.declinedBody', { company: iv.company_name })}</p>
                  </div>
                )}
                {iv.status === 'cancelled' && <p className="text-center text-gray-600 py-4" data-testid="interview-cancelled">{t('interviews.public.cancelled')}</p>}
              </>
            )}
        </Card>
        <p className="text-center text-xs text-blue-200 mt-6">Tavares Groupe — Sociedade Unipessoal Lda · info@trabadju.cv</p>
      </div>
    </main>
  );
}
